import { FunctionComponent, useMemo } from 'react';
import cn from 'classnames';
import { Design, ProductType, Shop } from '@/types';
import { fetchUser } from '@/utils/http/fetchUser';
import { t } from '@/utils/i18n';
import { getDesignImageUrl } from '@/utils/imageUrls/getDesignImageUrl';
import { getParams } from '@/utils/params';
import { LoadingImage } from '../LoadingImage';
import { FlagReport } from '../icons/FlagReport';
import { Inactive } from '../icons/Inactive';
import { Star } from '../icons/Star';
import { DesignPrice } from './DesignPrice';

interface DesignItemProps {
  onClick: (design: Design) => void;
  productType: ProductType;
  design: Design;
  imageHeight: number;
  isPrintable: boolean | undefined;
  isFavorited: boolean;
  isSelected: boolean;
  showDesignPrices?: boolean;
  toggleDesignFavorite: (design: Design, isFavorited: boolean) => void;
  context: Shop;
  isMarketplaceCategoryActive: boolean;
}

export const DesignItem: FunctionComponent<DesignItemProps> = ({
  onClick,
  design,
  isPrintable = false,
  isFavorited,
  isSelected,
  imageHeight,
  toggleDesignFavorite,
  showDesignPrices = false,
  context,
  isMarketplaceCategoryActive,
  productType,
}) => {
  const isLight = Boolean(design.backgroundColor);
  const imageClasses = cn('design-item__image', {
    'design-item__image--light': isLight,
    'design-item__image--selected': isSelected,
  });

  const imageStyle = useMemo(() => ({ height: `${imageHeight + 16}px` }), [imageHeight]);

  const imageUrl = getDesignImageUrl(design.id, { height: 150 });
  const favoriteClasses = cn('design-item__favorite', {
    'design-item__favorite--active': isFavorited,
    'design-item__action--light': isLight,
  });

  const openReportPopUp = getParams().openReportPopUp;
  const hasOpenReportPopUp = Boolean(openReportPopUp) && Boolean(design.ideaId);

  const handleFlagReportClick = async () => {
    const {
      name,
      id: designId,
      ideaId = '',
      user: { id: designerId },
    } = design;
    const { name: designerName } = await fetchUser(designerId);
    openReportPopUp!({
      name,
      designId,
      ideaId,
      designerName,
      color: isLight ? '#999' : '#f2f2f2',
    });
  };

  return (
    <>
      <button
        type="button"
        className="design-item__wrapper"
        title={design.name}
        disabled={!isPrintable}
        onClick={() => onClick(design)}
      >
        <div className="design-item__inner">
          <div className={imageClasses} style={imageStyle}>
            <LoadingImage src={imageUrl} alt="" className="design-item__img" />
            {!isPrintable && (
              <div className="design-item__overlay">
                <Inactive width="2em" height="2em" />
              </div>
            )}
          </div>

          <div className="design-item__info">
            {showDesignPrices && (
              <DesignPrice
                className="design-item__price ellipsis"
                zeroClassName="design-item__price--free"
                design={design}
                context={context}
                zeroValue={t('designSelector.free')}
                isMarketplaceCategoryActive={isMarketplaceCategoryActive}
                productType={productType}
              />
            )}
          </div>
        </div>
      </button>
      {/* aria-selected was recommended by usability agency */}
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <button
        type="button"
        title={t(isFavorited ? 'favorites.remove' : 'favorites.add', design.name)}
        aria-selected={isFavorited}
        className={favoriteClasses}
        onClick={() => toggleDesignFavorite(design, isFavorited)}
      >
        {isFavorited ? (
          <Star type="filled" className="design-item__favorite-icon" />
        ) : (
          <Star type="default" className="design-item__favorite-icon" />
        )}
      </button>
      {hasOpenReportPopUp && (
        <button
          type="button"
          title={t('designSelector.reportDesign')}
          className="design-item__flagReport"
          onClick={handleFlagReportClick}
        >
          <FlagReport className="design-item__flagReport-icon" width="1.5em" height="1.5em" />
        </button>
      )}
    </>
  );
};
